import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { setFocusedAssetInfo } from 'js/src/data/scenes/scenes-actions';

export interface HtmlDesignTarget {
    sceneId: string;
    assetId: string;
}

/**
 * What the graphic contains, not how it moves. A motion field here would commit the agent to an
 * arrival order before any technique had been read.
 */
export interface ScenePlan {
    text: string[];
    graphics: string[];
    layout: string;
}

/**
 * `activeConversationTarget` is the motion graphics scene currently in edit mode: the scene others
 * dim around, that shows the composition scrubber and its selectable parts.
 */
export interface HtmlDesignState {
    activeConversationTarget: HtmlDesignTarget | null;
    planBySceneId: Record<string, ScenePlan>;
    /** Read when assigning the next scene, so a video varies its techniques. */
    techniquesBySceneId: Record<string, string[]>;
}

const initialState: HtmlDesignState = {
    activeConversationTarget: null,
    planBySceneId: {},
    techniquesBySceneId: {},
};

const htmlDesignSlice = createSlice({
    name: 'htmlDesign',
    initialState,
    reducers: {
        setActiveHtmlDesign(state, action: PayloadAction<HtmlDesignTarget>) {
            state.activeConversationTarget = action.payload;
        },
        clearActiveHtmlDesign(state) {
            state.activeConversationTarget = null;
        },
        setHtmlDesignPlan(
            state,
            action: PayloadAction<{ sceneId: string; plan: ScenePlan; techniques: string[] }>,
        ) {
            state.planBySceneId[action.payload.sceneId] = action.payload.plan;
            state.techniquesBySceneId[action.payload.sceneId] = action.payload.techniques;
        },
    },
    extraReducers: (builder) => {
        // Focusing any luminary asset — a caption, the watermark, a sticker — means the user has moved
        // on to editing that asset, so a codeo scene in edit mode leaves it rather than leaving two
        // editing surfaces open at once.
        builder.addCase(setFocusedAssetInfo, (state) => {
            state.activeConversationTarget = null;
        });
    },
});

export const { setActiveHtmlDesign, clearActiveHtmlDesign, setHtmlDesignPlan } =
    htmlDesignSlice.actions;
export const htmlDesignReducer = htmlDesignSlice.reducer;
